import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { ProviderType } from "components/integrations/managed_integrations/types";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { pluralise } from "lib/utils";
import { useDownloadCustomerDocuments } from "../../lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { DeleteDocumentType } from "../../lib/types/graphQLEnums";
import { DeleteCustomerDocumentModal } from "./modals/DeleteCustomerDocumentModal";
import { MoveCustomerDocumentModal } from "./modals/MoveCustomerDocumentModal";
import ContentLibraryAiChatModal from "./ContentLibraryAiChatModal";

type Props = {
  selectedDocuments: CustomerDocumentDetails[];
  provider: ProviderType | "uploads";
  hasPermissionsToModify?: boolean;
  onClearSelection: () => void;
};

export default function ContentLibraryActionBar({
  selectedDocuments,
  provider,
  hasPermissionsToModify = true,
  onClearSelection,
}: Props) {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isAskAiModalOpen, setIsAskAiModalOpen] = useState(false);
  const [isMoveModalOpen, setMoveModalOpen] = useState(false);

  const { mutate: downloadDocuments, isLoading: isDownloading } = useDownloadCustomerDocuments({
    onSuccess: async (_data, variables) => {
      const downloadedFiles = variables.documents;

      if (downloadedFiles.length === 1) {
        toast.success(`Downloaded document ${downloadedFiles[0].fileName}`);
      } else {
        toast.success(`Downloaded ${downloadedFiles.length} documents`);
      }

      onClearSelection(); // Clear selection after deletion
    },
    onError: (_err, vars) =>
      toast.error(`Failed to download ${pluralise(vars.documents.length, "document")}`),
  });

  return (
    <>
      <Container aria-label="Content library bulk action bar">
        <Flex gap={12} alignItems="center">
          <Text color="white">{`${selectedDocuments.length} selected:`}</Text>
          <ActionButtonWrapper onClick={() => setIsAskAiModalOpen(true)}>
            <Button iconProps={{ size: 16, color: "white" }} icon="stars2">
              Ask Stotles AI
            </Button>
          </ActionButtonWrapper>
          {hasPermissionsToModify && (
            <>
              <ActionButtonWrapper onClick={() => setMoveModalOpen(true)}>
                <Button iconProps={{ size: 16, color: "white" }} icon="move">
                  Move
                </Button>
              </ActionButtonWrapper>
              <Button
                aria-label="Download selected documents"
                icon="download01"
                buttonType="tertiary"
                buttonStyle="inverse"
                loading={isDownloading}
                onClick={() =>
                  downloadDocuments({
                    documents: selectedDocuments,
                    zipFileName: `stotles_content_library_${new Date().toISOString().slice(0, 10)}.zip`,
                  })
                }
              />
              <Button
                aria-label="Delete selected documents"
                icon="trash01"
                buttonType="tertiary"
                buttonStyle="inverse"
                onClick={() => setDeleteModalOpen(true)}
              />
            </>
          )}
        </Flex>
        <Flex gap={8} alignItems="center">
          <Button aria-label="Close" icon="xClose" onClick={onClearSelection} />
        </Flex>
      </Container>
      <DeleteCustomerDocumentModal
        selectedDocuments={selectedDocuments}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={() => {
          onClearSelection();
          setDeleteModalOpen(false);
        }}
        documentType={DeleteDocumentType.ContentLibrary}
      />
      <ContentLibraryAiChatModal
        isOpen={isAskAiModalOpen}
        onClose={() => {
          onClearSelection();
          setIsAskAiModalOpen(false);
        }}
        provider={provider}
        selectedIds={selectedDocuments.map((doc) => doc.id)}
      />
      <MoveCustomerDocumentModal
        selectedDocuments={selectedDocuments}
        isOpen={isMoveModalOpen}
        onOpenChange={setMoveModalOpen}
        onSuccess={() => {
          onClearSelection();
          setMoveModalOpen(false);
        }}
      />
    </>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  height: 48,
  padding: 16,
  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));
